import type { Platform, PointerInput, ModuleId, TrialRaw, ModuleResult, SessionResult, RankEntry } from './types';
import { randUniform, shuffleInPlace } from './stats';
import { scoreModule, composeSession } from './scoring';
import { loadRanks, saveSessionRank, bestScore } from './rank';

type Screen =
  | 'hub'
  | 'module_intro'
  | 'module_run'
  | 'module_done'
  | 'report'
  | 'rank';

type Phase =
  | 'idle'
  | 'wait'
  | 'stim'
  | 'feedback'
  | 'nogo_hold';

interface Button {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
}

const MODULE_ORDER: ModuleId[] = ['srt', 'gng', 'crt', 'aim'];
const MODULE_TITLE: Record<ModuleId, string> = {
  srt: 'SRT · 简单反应',
  gng: 'Go/No-Go · 抑制反应',
  crt: 'CRT · 选择反应',
  aim: 'Aim · 视动瞄准',
};
const MODULE_HINT: Record<ModuleId, string> = {
  srt: '红屏等待，变绿立刻点击。提前点算犯规。',
  gng: '绿色点；红色不要点。',
  crt: '左侧亮点左侧，右侧亮点右侧。',
  aim: '尽快点中随机出现的靶点，共 30 个。',
};

const DELAY_MIN = 1000;
const DELAY_MAX = 4000;
const NOGO_WINDOW = 800;
const SRT_NEED = 5;
const SRT_MAX_FOUL = 3;
const GNG_FORMAL = 20;
const CRT_FORMAL = 16;
const AIM_NEED = 30;
const PRACTICE = 2;

export class App {
  private screen: Screen = 'hub';
  private moduleIndex = 0;
  private phase: Phase = 'idle';
  private practiceLeft = 0;
  private formalDone = 0;
  private trials: TrialRaw[] = [];
  private results: ModuleResult[] = [];
  private session: SessionResult | null = null;
  private ranks: RankEntry[] = [];
  private buttons: Button[] = [];
  private message = '';
  private subMessage = '';
  private stimAt = 0;
  private waitUntil = 0;
  private foulCount = 0;
  private gngQueue: Array<'go' | 'nogo'> = [];
  private crtQueue: Array<'left' | 'right'> = [];
  private currentKind: 'go' | 'nogo' | 'left' | 'right' | null = null;
  private aimTarget: { x: number; y: number; r: number } | null = null;
  private feedbackUntil = 0;
  private feedbackText = '';
  private feedbackColor = '#fff';
  private nogoResolved = false;
  private lastHit: { x: number; y: number; t: number } | null = null;

  constructor(private platform: Platform) {
    this.ranks = loadRanks(platform);
    platform.onFrame((t) => this.tick(t));
    platform.onPointer((e) => this.onPointer(e));
  }

  private W() {
    return this.platform.getSize().width;
  }
  private H() {
    return this.platform.getSize().height;
  }
  private S() {
    return Math.min(this.W(), this.H()) / 400;
  }

  private currentModule(): ModuleId {
    return MODULE_ORDER[this.moduleIndex];
  }

  private tick(now: number) {
    if (this.screen === 'module_run') {
      if (this.phase === 'wait' && now >= this.waitUntil) {
        this.showStimulus(now);
      } else if (this.phase === 'nogo_hold' && now >= this.stimAt + NOGO_WINDOW && !this.nogoResolved) {
        this.nogoResolved = true;
        this.finishTrial({
          practice: this.practiceLeft > 0,
          foul: false,
          kind: 'nogo',
          correct: true,
        }, now);
      } else if (this.phase === 'feedback' && now >= this.feedbackUntil) {
        this.nextTrialOrFinish();
      }
    }
    this.draw(now);
  }

  private startBattery() {
    this.moduleIndex = 0;
    this.results = [];
    this.session = null;
    this.enterModuleIntro();
  }

  private enterModuleIntro() {
    this.screen = 'module_intro';
    this.message = MODULE_TITLE[this.currentModule()];
    this.subMessage = MODULE_HINT[this.currentModule()];
  }

  private beginModule() {
    this.screen = 'module_run';
    this.trials = [];
    this.foulCount = 0;
    this.formalDone = 0;
    this.practiceLeft = PRACTICE;
    this.phase = 'idle';
    this.aimTarget = null;
    this.gngQueue = this.buildGngQueue();
    this.crtQueue = this.buildCrtQueue();
    this.scheduleTrial();
  }

  private buildGngQueue(): Array<'go' | 'nogo'> {
    const nogoCount = 6;
    const q: Array<'go' | 'nogo'> = [];
    for (let i = 0; i < GNG_FORMAL - nogoCount; i++) q.push('go');
    for (let i = 0; i < nogoCount; i++) q.push('nogo');
    return shuffleInPlace(q);
  }

  private buildCrtQueue(): Array<'left' | 'right'> {
    const q: Array<'left' | 'right'> = [];
    for (let i = 0; i < CRT_FORMAL / 2; i++) {
      q.push('left');
      q.push('right');
    }
    return shuffleInPlace(q);
  }

  private scheduleTrial() {
    const mod = this.currentModule();
    if (mod === 'aim') {
      this.spawnAim(this.platform.now());
      return;
    }
    this.phase = 'wait';
    this.currentKind = null;
    this.nogoResolved = false;
    this.waitUntil = this.platform.now() + randUniform(DELAY_MIN, DELAY_MAX);
    this.message = this.practiceLeft > 0 ? '练习' : '正式';
  }

  private showStimulus(now: number) {
    const mod = this.currentModule();
    this.stimAt = now;
    this.phase = 'stim';
    if (mod === 'gng') {
      const isPractice = this.practiceLeft > 0;
      if (isPractice) {
        this.currentKind = Math.random() < 0.7 ? 'go' : 'nogo';
      } else {
        this.currentKind = this.gngQueue[this.formalDone] ?? 'go';
      }
      if (this.currentKind === 'nogo') {
        this.phase = 'nogo_hold';
        this.nogoResolved = false;
      }
    } else if (mod === 'crt') {
      const isPractice = this.practiceLeft > 0;
      if (isPractice) {
        this.currentKind = Math.random() < 0.5 ? 'left' : 'right';
      } else {
        this.currentKind = this.crtQueue[this.formalDone] ?? 'left';
      }
    } else {
      this.currentKind = 'go';
    }
  }

  private spawnAim(now: number) {
    const w = this.W();
    const h = this.H();
    const marginX = w * 0.08;
    const marginY = h * 0.18;
    const r = Math.min(w, h) * 0.045;
    this.aimTarget = {
      x: randUniform(marginX + r, w - marginX - r),
      y: randUniform(marginY + r, h - marginY - r),
      r,
    };
    this.stimAt = now;
    this.phase = 'stim';
    this.message = this.practiceLeft > 0 ? '练习瞄准' : `靶 ${this.formalDone + 1}/${AIM_NEED}`;
  }

  private finishTrial(trial: TrialRaw, now: number) {
    if (trial.practice) {
      this.trials.push(trial);
      this.practiceLeft--;
      this.feedbackText = trial.foul ? '犯规' : trial.correct === false ? '错误' : `${(trial.rtMs ?? 0).toFixed(0)} ms`;
      this.feedbackColor = trial.foul || trial.correct === false ? '#e85d5d' : '#5dcf8a';
      this.phase = 'feedback';
      this.feedbackUntil = now + 450;
      return;
    }

    this.trials.push(trial);
    if (this.currentModule() === 'srt' && trial.foul) this.foulCount++;
    if (!trial.foul || this.currentModule() === 'gng' || this.currentModule() === 'crt') {
      // formal progress: SRT only counts valid; others count every formal trial
    }
    if (this.currentModule() === 'srt') {
      if (!trial.foul && trial.rtMs != null) this.formalDone++;
    } else if (this.currentModule() === 'aim') {
      if (trial.rtMs != null) this.formalDone++;
    } else {
      this.formalDone++;
    }

    this.feedbackText = trial.foul
      ? '犯规'
      : trial.correct === false
        ? '错误'
        : trial.rtMs != null
          ? `${trial.rtMs.toFixed(0)} ms`
          : '好';
    this.feedbackColor = trial.foul || trial.correct === false ? '#e85d5d' : '#5dcf8a';
    this.phase = 'feedback';
    this.feedbackUntil = now + 400;
  }

  private nextTrialOrFinish() {
    const mod = this.currentModule();
    if (mod === 'srt') {
      if (this.foulCount > SRT_MAX_FOUL) {
        this.completeModule();
        return;
      }
      if (this.formalDone >= SRT_NEED && this.practiceLeft <= 0) {
        this.completeModule();
        return;
      }
    } else if (mod === 'gng') {
      if (this.practiceLeft <= 0 && this.formalDone >= GNG_FORMAL) {
        this.completeModule();
        return;
      }
    } else if (mod === 'crt') {
      if (this.practiceLeft <= 0 && this.formalDone >= CRT_FORMAL) {
        this.completeModule();
        return;
      }
    } else if (mod === 'aim') {
      if (this.practiceLeft <= 0 && this.formalDone >= AIM_NEED) {
        this.completeModule();
        return;
      }
      // practice: 2 hits then formal
      if (this.practiceLeft > 0) {
        // practiceLeft decremented in finishTrial when practice
      }
    }
    this.scheduleTrial();
  }

  private completeModule() {
    const mod = this.currentModule();
    const result = scoreModule(mod, this.trials);
    this.results.push(result);
    this.screen = 'module_done';
    this.message = MODULE_TITLE[mod];
    this.subMessage = result.valid
      ? `均值 ${result.meanMs ?? '—'} ms · 得分 ${result.score}`
      : `无效成绩，得分 0（可继续）`;
  }

  private advanceFromModuleDone() {
    if (this.moduleIndex < MODULE_ORDER.length - 1) {
      this.moduleIndex++;
      this.enterModuleIntro();
    } else {
      this.session = composeSession(this.results, this.platform.kind);
      this.ranks = saveSessionRank(this.platform, this.session);
      this.screen = 'report';
    }
  }

  private onPointer(e: PointerInput) {
    if (e.type !== 'down') return;
    this.lastHit = { x: e.x, y: e.y, t: e.t };

    for (const b of this.buttons) {
      if (e.x >= b.x && e.x <= b.x + b.w && e.y >= b.y && e.y <= b.y + b.h) {
        this.onButton(b.id);
        return;
      }
    }

    if (this.screen === 'module_run') this.onRunPointer(e);
  }

  private onButton(id: string) {
    switch (id) {
      case 'start':
        this.startBattery();
        break;
      case 'rank':
        this.ranks = loadRanks(this.platform);
        this.screen = 'rank';
        break;
      case 'back_hub':
        this.screen = 'hub';
        break;
      case 'begin_module':
        this.beginModule();
        break;
      case 'next_module':
        this.advanceFromModuleDone();
        break;
      case 'to_rank':
        this.ranks = loadRanks(this.platform);
        this.screen = 'rank';
        break;
      case 'retry':
        this.startBattery();
        break;
    }
  }

  private onRunPointer(e: PointerInput) {
    const mod = this.currentModule();
    const now = e.t;

    if (this.phase === 'wait') {
      this.finishTrial(
        {
          practice: this.practiceLeft > 0,
          foul: true,
        },
        now,
      );
      return;
    }

    if (this.phase === 'feedback') return;

    if (mod === 'srt' && (this.phase === 'stim')) {
      const isPractice = this.practiceLeft > 0;
      this.finishTrial(
        {
          practice: isPractice,
          foul: false,
          rtMs: now - this.stimAt,
          correct: true,
        },
        now,
      );
      return;
    }

    if (mod === 'gng') {
      if (this.phase === 'stim' && this.currentKind === 'go') {
        this.finishTrial(
          {
            practice: this.practiceLeft > 0,
            foul: false,
            kind: 'go',
            correct: true,
            rtMs: now - this.stimAt,
          },
          now,
        );
      } else if (this.phase === 'nogo_hold' && this.currentKind === 'nogo' && !this.nogoResolved) {
        this.nogoResolved = true;
        this.finishTrial(
          {
            practice: this.practiceLeft > 0,
            foul: true,
            kind: 'nogo',
            correct: false,
            rtMs: now - this.stimAt,
          },
          now,
        );
      }
      return;
    }

    if (mod === 'crt' && this.phase === 'stim') {
      const left = e.x < this.W() / 2;
      const wantLeft = this.currentKind === 'left';
      const ok = left === wantLeft;
      this.finishTrial(
        {
          practice: this.practiceLeft > 0,
          foul: !ok,
          kind: this.currentKind === 'left' ? 'left' : 'right',
          correct: ok,
          rtMs: now - this.stimAt,
        },
        now,
      );
      return;
    }

    if (mod === 'aim' && this.phase === 'stim' && this.aimTarget) {
      const dx = e.x - this.aimTarget.x;
      const dy = e.y - this.aimTarget.y;
      if (dx * dx + dy * dy <= this.aimTarget.r * this.aimTarget.r) {
        const isPractice = this.practiceLeft > 0;
        this.finishTrial(
          {
            practice: isPractice,
            foul: false,
            rtMs: now - this.stimAt,
            correct: true,
          },
          now,
        );
      }
    }
  }

  private draw(_now: number) {
    const ctx = this.platform.canvas.getContext('2d');
    if (!ctx) return;
    const w = this.W();
    const h = this.H();
    const s = this.S();
    this.buttons = [];

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);

    if (this.screen === 'hub') this.drawHub(ctx, w, h, s);
    else if (this.screen === 'module_intro') this.drawIntro(ctx, w, h, s);
    else if (this.screen === 'module_run') this.drawRun(ctx, w, h, s);
    else if (this.screen === 'module_done') this.drawModuleDone(ctx, w, h, s);
    else if (this.screen === 'report') this.drawReport(ctx, w, h, s);
    else if (this.screen === 'rank') this.drawRank(ctx, w, h, s);
  }

  private fillBg(ctx: CanvasRenderingContext2D, w: number, h: number, color: string) {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, w, h);
  }

  private text(
    ctx: CanvasRenderingContext2D,
    str: string,
    x: number,
    y: number,
    size: number,
    color: string,
    align: CanvasTextAlign = 'center',
  ) {
    ctx.fillStyle = color;
    ctx.font = `600 ${size}px "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif`;
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    ctx.fillText(str, x, y);
  }

  private addButton(
    ctx: CanvasRenderingContext2D,
    id: string,
    label: string,
    cx: number,
    cy: number,
    bw: number,
    bh: number,
    fill = '#2f6fed',
  ) {
    const x = cx - bw / 2;
    const y = cy - bh / 2;
    ctx.fillStyle = fill;
    roundRect(ctx, x, y, bw, bh, bh * 0.2);
    ctx.fill();
    this.text(ctx, label, cx, cy, bh * 0.38, '#fff');
    this.buttons.push({ id, x, y, w: bw, h: bh, label });
  }

  private drawHub(ctx: CanvasRenderingContext2D, w: number, h: number, s: number) {
    this.fillBg(ctx, w, h, '#0c1220');
    // soft gradient bands
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#152238');
    g.addColorStop(1, '#0c1220');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    this.text(ctx, '电竞体测', w / 2, h * 0.28, 42 * s, '#f2f6ff');
    this.text(ctx, '标准电竞反应体测包', w / 2, h * 0.36, 16 * s, '#8ea0c0');
    this.text(
      ctx,
      '结果含设备延迟，仅供横向比较与自我进步',
      w / 2,
      h * 0.42,
      12 * s,
      '#6a7a96',
    );

    const best = bestScore(this.platform);
    if (best) {
      this.text(
        ctx,
        `本地最佳 ${best.score} · ${best.rank}`,
        w / 2,
        h * 0.5,
        14 * s,
        '#a8c0ff',
      );
    }

    this.addButton(ctx, 'start', '开始体测', w / 2, h * 0.64, w * 0.55, 48 * s);
    this.addButton(ctx, 'rank', '本地榜', w / 2, h * 0.76, w * 0.4, 42 * s, '#243352');
  }

  private drawIntro(ctx: CanvasRenderingContext2D, w: number, h: number, s: number) {
    this.fillBg(ctx, w, h, '#0c1220');
    this.text(ctx, `第 ${this.moduleIndex + 1}/4 关`, w / 2, h * 0.22, 14 * s, '#8ea0c0');
    this.text(ctx, this.message, w / 2, h * 0.34, 28 * s, '#f2f6ff');
    wrapText(ctx, this.subMessage, w / 2, h * 0.44, w * 0.8, 16 * s, '#a8b6d0');
    this.addButton(ctx, 'begin_module', '开始（含 2 次练习）', w / 2, h * 0.62, w * 0.7, 48 * s);
  }

  private drawRun(ctx: CanvasRenderingContext2D, w: number, h: number, s: number) {
    const mod = this.currentModule();

    if (this.phase === 'wait') {
      this.fillBg(ctx, w, h, mod === 'srt' ? '#8b1e1e' : '#1a2236');
      this.text(ctx, mod === 'srt' ? '等待变绿…' : '准备…', w / 2, h / 2, 28 * s, '#ffd0d0');
      this.text(ctx, '勿提前点击', w / 2, h / 2 + 40 * s, 14 * s, '#ffb0b0');
    } else if (this.phase === 'feedback') {
      this.fillBg(ctx, w, h, '#121826');
      this.text(ctx, this.feedbackText, w / 2, h / 2, 36 * s, this.feedbackColor);
    } else if (mod === 'srt') {
      this.fillBg(ctx, w, h, '#1f8a4d');
      this.text(ctx, '点击！', w / 2, h / 2, 40 * s, '#e8ffe8');
    } else if (mod === 'gng') {
      const go = this.currentKind === 'go';
      this.fillBg(ctx, w, h, '#121826');
      ctx.fillStyle = go ? '#2ecc71' : '#e74c3c';
      const r = Math.min(w, h) * 0.12;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2);
      ctx.fill();
      this.text(ctx, go ? 'GO · 点' : 'NO · 别点', w / 2, h / 2 + r + 36 * s, 18 * s, '#ddd');
    } else if (mod === 'crt') {
      this.fillBg(ctx, w, h, '#121826');
      ctx.fillStyle = '#1e2a44';
      ctx.fillRect(0, 0, w / 2, h);
      ctx.fillStyle = '#162038';
      ctx.fillRect(w / 2, 0, w / 2, h);
      const left = this.currentKind === 'left';
      ctx.fillStyle = '#3d8bfd';
      const cx = left ? w * 0.25 : w * 0.75;
      ctx.beginPath();
      ctx.arc(cx, h / 2, Math.min(w, h) * 0.08, 0, Math.PI * 2);
      ctx.fill();
      this.text(ctx, '点对应半屏', w / 2, h * 0.18, 16 * s, '#9ab');
    } else if (mod === 'aim') {
      this.fillBg(ctx, w, h, '#0e1524');
      this.text(ctx, this.message, w / 2, h * 0.1, 14 * s, '#9ab');
      if (this.aimTarget) {
        ctx.fillStyle = '#ff5a5a';
        ctx.beginPath();
        ctx.arc(this.aimTarget.x, this.aimTarget.y, this.aimTarget.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(this.aimTarget.x, this.aimTarget.y, this.aimTarget.r * 0.28, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // progress header
    if (this.phase !== 'feedback') {
      const progress =
        mod === 'srt'
          ? `有效 ${Math.min(this.formalDone, SRT_NEED)}/${SRT_NEED}`
          : mod === 'aim'
            ? `命中 ${this.formalDone}/${AIM_NEED}`
            : mod === 'gng'
              ? `${this.formalDone}/${GNG_FORMAL}`
              : `${this.formalDone}/${CRT_FORMAL}`;
      this.text(
        ctx,
        `${MODULE_TITLE[mod]} · ${this.practiceLeft > 0 ? '练习' : progress}`,
        w / 2,
        h * 0.06,
        12 * s,
        'rgba(255,255,255,0.55)',
      );
    }
  }

  private drawModuleDone(ctx: CanvasRenderingContext2D, w: number, h: number, s: number) {
    this.fillBg(ctx, w, h, '#0c1220');
    const r = this.results[this.results.length - 1];
    this.text(ctx, '本关完成', w / 2, h * 0.2, 16 * s, '#8ea0c0');
    this.text(ctx, this.message, w / 2, h * 0.3, 24 * s, '#f2f6ff');
    this.text(ctx, this.subMessage, w / 2, h * 0.4, 16 * s, '#a8c0ff');
    if (r?.flags.includes('suspect')) {
      this.text(ctx, '⚠ 疑似异常，建议复测', w / 2, h * 0.48, 13 * s, '#f0c674');
    }
    const label =
      this.moduleIndex < MODULE_ORDER.length - 1 ? '下一关' : '查看总报告';
    this.addButton(ctx, 'next_module', label, w / 2, h * 0.62, w * 0.55, 48 * s);
  }

  private drawReport(ctx: CanvasRenderingContext2D, w: number, h: number, s: number) {
    this.fillBg(ctx, w, h, '#0c1220');
    const sess = this.session;
    if (!sess) return;
    this.text(ctx, '电竞体测 · 报告', w / 2, h * 0.1, 18 * s, '#8ea0c0');
    this.text(ctx, `${sess.score}`, w / 2, h * 0.22, 52 * s, '#f2f6ff');
    this.text(ctx, sess.rank, w / 2, h * 0.3, 22 * s, '#5dcf8a');
    if (sess.suspect) {
      this.text(ctx, '⚠ 含异常标记', w / 2, h * 0.36, 13 * s, '#f0c674');
    }

    let y = h * 0.44;
    for (const m of sess.modules) {
      const line = `${MODULE_TITLE[m.module]}  ${m.meanMs ?? '—'}ms  分${m.score}`;
      this.text(ctx, line, w / 2, y, 13 * s, '#a8b6d0');
      y += 22 * s;
    }

    this.text(ctx, '结果含设备延迟，不作医学/录用结论', w / 2, h * 0.72, 11 * s, '#6a7a96');
    this.addButton(ctx, 'to_rank', '本地榜', w / 2 - w * 0.22, h * 0.82, w * 0.36, 42 * s, '#243352');
    this.addButton(ctx, 'retry', '再测一次', w / 2 + w * 0.22, h * 0.82, w * 0.36, 42 * s);
  }

  private drawRank(ctx: CanvasRenderingContext2D, w: number, h: number, s: number) {
    this.fillBg(ctx, w, h, '#0c1220');
    this.text(ctx, '本地高分榜', w / 2, h * 0.1, 22 * s, '#f2f6ff');
    if (this.ranks.length === 0) {
      this.text(ctx, '暂无记录', w / 2, h * 0.4, 16 * s, '#8ea0c0');
    } else {
      let y = h * 0.2;
      this.ranks.slice(0, 12).forEach((r, i) => {
        const mark = r.suspect ? ' ⚠' : '';
        const srt = r.srtMeanMs != null ? `${r.srtMeanMs}ms` : '—';
        this.text(
          ctx,
          `${i + 1}. ${r.score} · ${r.rank} · SRT ${srt}${mark}`,
          w / 2,
          y,
          13 * s,
          '#a8b6d0',
        );
        y += 22 * s;
      });
    }
    this.addButton(ctx, 'back_hub', '返回大厅', w / 2, h * 0.88, w * 0.45, 44 * s, '#243352');
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  size: number,
  color: string,
) {
  ctx.fillStyle = color;
  ctx.font = `500 ${size}px "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const chars = text.split('');
  let line = '';
  let yy = y;
  for (const ch of chars) {
    const test = line + ch;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, yy);
      line = ch;
      yy += size * 1.4;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}
